import { useState } from 'react'

export default function HowItWorksPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null)

  const steps = [
    {
      number: '01',
      title: 'Athletes & Teams Partner with Certi',
      description: 'Professional athletes and sports organizations partner with Certi to offer their game-used memorabilia directly to fans. Each item is documented from the moment it leaves the field or court, ensuring a clear chain of custody.',
    },
    {
      number: '02',
      title: 'Expert Authentication',
      description: "Our authentication specialists thoroughly examine every piece of memorabilia. We verify provenance, inspect for authenticity markers, and document all unique characteristics to guarantee each item is genuine.",
    },
    {
      number: '03',
      title: 'NFC-Powered Digital Certificates',
      description: 'Every authenticated item receives a unique NFC chip that links to a blockchain-verified digital certificate. Simply tap your phone to instantly access the complete history, authentication details, and proof of ownership for your memorabilia.',
    },
    {
      number: '04',
      title: 'List on the Marketplace',
      description: 'Authenticated items are professionally photographed and listed on the Certi marketplace. Each listing includes detailed information, high-resolution images, and the digital certificate, giving buyers complete transparency.',
    },
    {
      number: '05',
      title: 'Secure Purchase & Ownership Transfer',
      description: "When you purchase an item, the digital certificate of authenticity is transferred directly to you. Your ownership is recorded on the blockchain, creating a permanent, verifiable record of the transaction.",
    },
    {
      number: '06',
      title: 'Fast & Secure Shipping',
      description: "Your authenticated memorabilia is carefully packaged and shipped with full insurance and tracking. Items typically arrive within 3-5 business days, ready to display with confidence in their authenticity.",
    },
  ]

  const faqs = [
    {
      question: "How does Certi verify authenticity?",
      answer: "Certi uses a multi-layered authentication process. First, we work directly with athletes, teams, and authorized sources to establish clear provenance. Our expert authenticators then examine each item for specific markers of authenticity. Finally, we assign a unique NFC chip linked to a blockchain-verified digital certificate that permanently records all authentication details, ownership history, and item specifications.",
    },
    {
      question: 'What is an NFC digital certificate?',
      answer: 'An NFC (Near Field Communication) digital certificate is a tamper-proof verification system. Each authenticated item has a unique NFC chip that you can tap with your smartphone to instantly view the complete authentication report, ownership history, and all verified details about the memorabilia. This certificate is stored on the blockchain, making it permanent and impossible to forge.',
    },
    {
      question: 'Can I resell items purchased on Certi?',
      answer: "Yes! When you purchase an item, you receive full ownership rights including the ability to resell. The digital certificate automatically transfers to the new owner, maintaining the complete chain of custody and authentication. This makes Certi items more valuable and easier to resell compared to traditional memorabilia.",
    },
    {
      question: 'What types of memorabilia does Certi offer?',
      answer: "Certi specializes in game-used sports memorabilia including jerseys, equipment, and other items directly from professional athletes and teams. Each item is authentic, verified, and comes with our NFC-backed digital certificate of authenticity.",
    },
  ]

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-gray-50 via-white to-gray-50 py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-extrabold text-gray-900 mb-6 tracking-tight">
            How Certi Works
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 leading-relaxed font-light">
            The most common question we're asked is: <span className="font-medium text-gray-900">"How does Certi work?"</span>
          </p>
          <p className="text-lg text-gray-600 leading-relaxed mt-6 max-w-3xl mx-auto">
            Certi makes it easy to buy, sell, and collect authentic, game-used sports memorabilia with full confidence. Every item on Certi is verified through a secure authentication process and backed by a digital certificate, so collectors always know exactly what they're getting. From listing to delivery, Certi ensures transparency, traceability, and trust at every step.
          </p>
        </div>
      </div>

      {/* Process Steps */}
      <div className="max-w-5xl mx-auto py-20 px-4">
        <div className="space-y-16">
          {steps.map((step, index) => (
            <div
              key={index}
              className="flex flex-col md:flex-row gap-8 items-start group"
            >
              {/* Step Number */}
              <div className="flex-shrink-0">
                <div className="relative">
                  <div className="w-20 h-20 rounded-2xl bg-gray-900 flex items-center justify-center shadow-lg group-hover:shadow-xl group-hover:bg-brand-primary transition-all duration-300">
                    <span className="text-3xl font-extrabold text-white drop-shadow-lg">{step.number}</span>
                  </div>
                  {/* Connector Line */}
                  {index < steps.length - 1 && (
                    <div className="absolute top-24 left-1/2 w-0.5 h-16 bg-gradient-to-b from-gray-300 to-transparent -translate-x-1/2"></div>
                  )}
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 pt-2">
                <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-brand-primary transition-colors duration-300">
                  {step.title}
                </h3>
                <p className="text-lg text-gray-600 leading-relaxed">
                  {step.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* FAQ Section */}
      <div className="bg-gray-50 py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold text-gray-900 mb-4 text-center">
            Frequently Asked Questions
          </h2>
          <p className="text-lg text-gray-600 text-center mb-12">
            Everything you need to know about Certi
          </p>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden transition-all duration-300 hover:shadow-md"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full px-6 py-5 flex items-center justify-between text-left"
                >
                  <span className="text-lg font-semibold text-gray-900 pr-8">
                    {faq.question}
                  </span>
                  <svg
                    className={`w-6 h-6 text-brand-primary flex-shrink-0 transition-transform duration-300 ${
                      openFaq === index ? 'rotate-180' : ''
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>

                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    openFaq === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="px-6 pb-5">
                    <div className="pt-2 text-gray-600 leading-relaxed whitespace-pre-line border-t border-gray-100">
                      {faq.answer}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Ready to start collecting?
          </h2>
          <p className="text-xl text-gray-700 mb-8">
            Explore authentic game-worn memorabilia on Certi
          </p>
          <a
            href="/marketplace"
            className="inline-block bg-brand-primary hover:bg-brand-primary/90 text-white font-semibold px-8 py-4 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
          >
            Browse Marketplace
          </a>
        </div>
      </div>
    </div>
  )
}
